/* eslint-disable import/no-cycle */
import { store } from '@/store';
import {
  ActiveLiveEditorItemData,
  ElementPartTypeEnum,
  HistoryType,
  TemplateStructureEnum,
  Translation,
  WidgetTypeEnum,
} from '@/types';
import {
  addNewSectionToState,
  addNewSyncElementToSection,
  addTranslationsToState,
  hideLeftToolbar,
  moveWidgetFromSectionToAnother,
  refreshSectionsConfig,
  refreshTranslations,
  resetActiveSection,
  resetIframe,
  restoreSmartListsOriginalCode,
  setActiveSection,
  TemplateEditorState, updateFormsInState, updateSectionsInState, validateWidgetAddition,
} from '@/composables/template-editor/TemplateEditor';
import {
  br2nl, getParents, nl2br, showToastError,
} from '@/helpers';
import { checkIfSavePointNeeded, createHistory } from '@/composables/template-editor/History';
import { Maybe } from '@/types/generated-types/graphql';
import { getShopDefaultLang, Lang, UserState } from '@/composables/User';
import {
  addTranslation,
  createNewStylesheet,
  decorateElementWithStyles,
  generateUniqStructureId,
  getCssOfElement,
  getElementFromIframe,
  getElementResetStylesByType,
  getSectionParent,
  getTemplateIframeDocument,
  getTemplateParentTypeByType,
  getWidgetType,
  isDisplayTemplate,
  isEmailTemplate,
  changeChildItemsId,
} from './helpers';
import { initWidget } from './widget-init-registry';
import {
  ACTIONS_BUTTON_GROUP,
  BUILDER_ELEMENT_ACTIVE_CLASS,
  BUILDER_ELEMENT_HOVER_CLASS,
  CUSTOM_WIDGET_CSS_PREFIX,
  MAX_COLUMN_COUNT,
  NEW_SECTION_ID_PREFIX,
  TEMPLATE_COLUMN_IDENTIFIER_PREFIX,
  TEMPLATE_LINE_CLASS,
  TEMPLATE_LINE_IDENTIFIER_PREFIX,
  TEMPLATE_PART_ELEMENT_IDENTIFIER,
  TEMPLATE_PART_ELEMENT_PART_TYPE,
  TEMPLATE_SECTION_CLASS,
  TEMPLATE_SECTION_CONTENT_IDENTIFIER,
  TEMPLATE_SECTION_IDENTIFIER_PREFIX,
  TEMPLATE_STRUCTURE_MAP,
  TEMPLATE_SYNC_ELEMENT_CLASS,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
  TEMPLATE_SYNC_ELEMENT_NAME,
  TEMPLATE_WIDGET_IDENTIFIER_PREFIX,
  TRANSLATION_ATTRIBUTE,
  WIDGET_DROPPABLE_CLASS,
} from './constants';
import { hoverResetStyles } from './elements-style-definitions';
import { executeWhenDragComplete, resetListeners } from './listeners';

// eslint-disable-next-line import/no-cycle
import { addOrUpdateDynamicStyle, cleanupDynamicStyle, getDynamicStylesForSelector, getTemplateColumns, getTemplateSections } from './parser';
import { cleanupAllUnusedWidgetCss } from './widget-css-manager';
import {
  getDisplayedElements, getSmartListContent, getSmartListTitle,
} from '../getters';
import {
  setDisplayedElements, setSmartListButtonTitle, setSmartListTitle,
} from '../setters';

const removeChildActionsButtonGroup = (parentElement: HTMLElement) => {
  const actionsButtonGroup = parentElement.querySelectorAll(`.${ACTIONS_BUTTON_GROUP}`);
  (actionsButtonGroup ?? []).forEach((element) => {
    try {
      parentElement.removeChild(element);
    } catch (e) {
      // Do nothing
    }
  });
};

const setClonedSectionContentAttributes = (section: HTMLElement) => {
  const sectionContent = section.querySelector(TEMPLATE_SECTION_CONTENT_IDENTIFIER) as HTMLElement;
  if (sectionContent) {
    const styles = getDynamicStylesForSelector(`#${sectionContent.id}`);
    decorateElementWithStyles(sectionContent, hoverResetStyles);
    addOrUpdateDynamicStyle(`#${sectionContent.id}`, styles);
  }
};

const setChildColumnsWidth = (columnList: HTMLElement[]) => {
  const columnWidth = 100 / columnList.length;

  columnList.forEach((column: HTMLElement) => {
    // eslint-disable-next-line no-param-reassign
    column.style.width = `${columnWidth}%`;
  });
};

/**
 * Duplicate element, generate new ID and apply reset styles
 * @param type
 * @param element
 */
const duplicateHtmlOfElement = (type: TemplateStructureEnum, element: HTMLElement): HTMLElement => {
  let prefix;
  let resetStyles = {};

  // Create new element
  const clonedElement = element.cloneNode(true) as HTMLElement;

  switch (type) {
    case TemplateStructureEnum.SECTION:
      prefix = TEMPLATE_SECTION_IDENTIFIER_PREFIX;
      resetStyles = {
        ...hoverResetStyles,
      };
      break;
    case TemplateStructureEnum.EMPTY_SECTION:
      prefix = TEMPLATE_SECTION_IDENTIFIER_PREFIX;
      resetStyles = {
        ...hoverResetStyles,
      };
      break;
    case TemplateStructureEnum.LINE:
      prefix = TEMPLATE_LINE_IDENTIFIER_PREFIX;
      break;
    case TemplateStructureEnum.COLUMN:
      prefix = `${TEMPLATE_COLUMN_IDENTIFIER_PREFIX}_`;
      break;
    case TemplateStructureEnum.WIDGET:
      prefix = TEMPLATE_WIDGET_IDENTIFIER_PREFIX;
      resetStyles = {
        ...hoverResetStyles,
      };
      break;
    default:
      prefix = TEMPLATE_WIDGET_IDENTIFIER_PREFIX;
      resetStyles = {
        ...hoverResetStyles,
      };
      break;
  }

  // Change element ID
  const newId = `${prefix}${generateUniqStructureId()}`;
  const oldId = clonedElement.id;
  clonedElement.id = newId;

  // Update form id
  clonedElement.innerHTML = clonedElement.innerHTML.replaceAll(oldId, newId);

  if (type === TemplateStructureEnum.SECTION || type === TemplateStructureEnum.EMPTY_SECTION) {
    const tmpElementId = `${NEW_SECTION_ID_PREFIX}${generateUniqStructureId()}`;
    // We remove useless data attributes and add a temporary template_element_id
    clonedElement.removeAttribute('data-spm_element_hash');
    clonedElement.removeAttribute('data-spmelementid');
    clonedElement.removeAttribute('data-spm_element_sync');
    clonedElement.setAttribute('data-spmelementid', tmpElementId);

    // We set the section as the active section in the state
    setActiveSection(tmpElementId);
  }

  // Apply reset CSS for builder CSS
  const resetActiveItemStyles = getElementResetStylesByType(type);
  decorateElementWithStyles(clonedElement, {
    ...resetActiveItemStyles,
    ...resetStyles,
  });

  return clonedElement;
};

/**
 * Append CSS to a specific stylesheet type
 * @param sectionId Section ID for the stylesheet
 * @param css CSS content to append
 * @param type Stylesheet type (dynamic, dynamic_persistent, etc.)
 */
const appendCssToStylesheet = (sectionId: string, css: string, type: string) => {
  if (!css) return;

  const selector = type.includes('static')
    ? `style[data-spm-styles="${type}"]`
    : `style[data-spm-styles="${type}"][data-spm-section="${sectionId}"]`;
  let stylesheet = getElementFromIframe(selector);
  if (!stylesheet) {
    stylesheet = createNewStylesheet(type, sectionId);
  }

  if (stylesheet) {
    stylesheet.append(css);
  }
};

/**
 * Duplicate all CSS rules of an element and set the new element ID
 * @param sectionId
 * @param oldElementId
 * @param newElementId
 * @param cssString
 */
export const duplicateCssOfElement = (
  sectionId: string,
  oldElementId: string,
  newElementId: string,
  cssString: Maybe<string> = null,
  customCss: Maybe<Record<string, any>> = null,
) => {
  const createStylesheet = (type = 'dynamic', customCssString = '') => {
    // Create CSS stylesheet if doesn't exist

    const selector = type.includes('static')
      ? `style[data-spm-styles="${type}"]`
      : `style[data-spm-styles="${type}"][data-spm-section="${sectionId}"]`;
    let dynamicStylesheet = getElementFromIframe(selector);

    if (!dynamicStylesheet) {
      // Create dynamic stylesheet if it doesn't exist
      dynamicStylesheet = createNewStylesheet(type, sectionId);
    }
    // Get all CSS of element
    let dynamicStylesOfOldElement = cssString ?? getCssOfElement(oldElementId, type);
    if (customCssString) {
      dynamicStylesOfOldElement = customCssString;
    }
    if (typeof dynamicStylesOfOldElement === 'object') {
      dynamicStylesOfOldElement = '';
    }

    // Replace widget ID in CSS
    const reg = new RegExp(`${oldElementId}([\\s,>+~.:\\[\\{\\)]|$)`, 'g');
    const finalDynamicStyles = dynamicStylesOfOldElement.replace(reg, `${newElementId}$1`);
    // Append new CSS in dynamic stylesheet
    if (dynamicStylesheet && finalDynamicStyles) {
      dynamicStylesheet.append(finalDynamicStyles);
    }

    return finalDynamicStyles;
  };

  let createDynamicPersistent = false;
  const reg = new RegExp(`${oldElementId}(,?(\\.[a-zA-Z0-9_,-]*)? )`, 'gm');

  const dynamicStylesheetPersistent = getElementFromIframe(`style[data-spm-styles="dynamic_persistent"][data-spm-section="${sectionId}"]`);
  if (dynamicStylesheetPersistent && dynamicStylesheetPersistent.outerHTML.match(reg)) {
    createDynamicPersistent = true;
  }

  if (!customCss) {
    if (createDynamicPersistent) {
      createStylesheet('dynamic_persistent');
    }
    return createStylesheet();
  }
  let returnCss = '';
  Object.keys(customCss).forEach((key) => {
    returnCss += createStylesheet(key, customCss[key]);
  });
  return returnCss;
};

/**
 * Duplicate and change keys of all translation keys inside an element (section/row/column/widget)
 * @param element
 * @param translations
 * @param sectionId
 */
const duplicateTranslationsOfElement = (element: HTMLElement, translations: Maybe<Record<string, any>> = null, sectionId: Maybe<string> = null) => {
  const regExpTranslations = new RegExp('LANG_[a-zA-Z0-9.]+', 'g');

  // Wrap element to get complete HTML
  const wrap = document.createElement('div');
  wrap.appendChild(element.cloneNode(true));

  // Matches all translation keys inside element
  const matches = wrap.innerHTML.matchAll(regExpTranslations);

  if (matches) {
    const keyMapping: any = {}; // Store the mapping of old and new keys
    let translationToAddToState: Translation[] = [];
    const clonedTranslations = translations ? { ...translations } : null; // Clone translations of element if exists
    const shopLanguages = UserState.activeShop?.langs.map((language: Lang) => language.id) ?? [];

    if (clonedTranslations) {
      // Create translations for missing languages if any
      const availableLanguagesInTranslations = Object.keys(clonedTranslations);
      const defaultLanguage = getShopDefaultLang();
      const missingLanguages = shopLanguages.filter((lang) => !availableLanguagesInTranslations.includes(lang));

      if (missingLanguages.length > 0) {
        // We have missing languages in element's translations, we copy default language, or at least the first available language
        const languageToCopy = availableLanguagesInTranslations.includes(defaultLanguage) ? defaultLanguage : availableLanguagesInTranslations[0];

        missingLanguages.forEach((missingLanguage) => {
          clonedTranslations[missingLanguage] = clonedTranslations[languageToCopy];
        });
      }
    }

    // Loop on each translation key to generate a new one and store in the state
    Array.from(matches, (m) => m[0]).forEach((match) => {
      const key = match.split('.')[0].replace('LANG_', '');

      // Generate a new key in the mapping
      if (!Object.getOwnPropertyDescriptor(keyMapping, key)) {
        keyMapping[key] = generateUniqStructureId();
      }

      if (clonedTranslations) {
        // Change keys in translations object according to new keys, and store them in state
        Object.keys(clonedTranslations).forEach((language: string) => {
          clonedTranslations[language][keyMapping[key]] = clonedTranslations[language][key];
          // delete old key
          delete clonedTranslations[language][key];
          translationToAddToState.push(
            ...(addTranslation(clonedTranslations[language], keyMapping[key], sectionId ?? '', language, TemplateEditorState.template?.type ?? 'email') ?? []),
          );
        });
      } else {
        // Get the translations matching the current key
        const translationsInState = TemplateEditorState.template?.translations.filter((trl: Translation) => trl.key === match.replace('LANG_', '')) ?? [];

        // Create new translations based on new keys
        translationsInState.forEach((trl: Translation) => {
          const newTranslation = { ...trl };
          const currentKey = newTranslation.key.split('.');
          currentKey[0] = keyMapping[key];
          newTranslation.key = currentKey.join('.');

          if (sectionId) {
            newTranslation.section = sectionId;
          }

          translationToAddToState.push(newTranslation);
        });
      }

      // eslint-disable-next-line no-param-reassign
      element.innerHTML = element.innerHTML.replace(key, keyMapping[key]);
    });

    // We search for translations in HTML not in translations object
    const regExpMissingTranslationsAttributes = new RegExp(`${TRANSLATION_ATTRIBUTE}-([^=]+)="LANG_([^"]+)"`, 'g');
    const regExpMissingTranslationsText = new RegExp('{LANG_([^}]+)}', 'g');
    const matchesTranslationsAsAttributes = element.innerHTML.matchAll(regExpMissingTranslationsAttributes);
    const matchesTranslationsAsText = element.innerHTML.matchAll(regExpMissingTranslationsText);

    // Loop on matched translations as attributes
    Array.from(matchesTranslationsAsAttributes).forEach((match) => {
      const attribute = match[1];
      const key = match[2];
      const found = translationToAddToState.filter((translation) => translation.key === key);

      if (found.length === 0) {
        // Key is not in the array of translations to add to state
        const elementWithTranslation = element.querySelector(`[${TRANSLATION_ATTRIBUTE}-${match[1]}="LANG_${key}"]`);
        let valueOfTranslation = '';

        if (elementWithTranslation && elementWithTranslation.hasAttribute(`${match[1]}`)) {
          valueOfTranslation = elementWithTranslation.getAttribute(`${match[1]}`) ?? '';
        }

        let fieldType = 'text';

        switch (attribute) {
          case 'href':
            fieldType = 'link';
            break;
          case 'src':
            fieldType = 'image';
            break;
          case 'data-redirect':
          case 'placeholder':
            fieldType = attribute;
            break;
          default:
            break;
        }

        let callback = (value: string) => value;
        let translationValue = valueOfTranslation;
        if (valueOfTranslation.match(/^base64:/)) {
          callback = (value: string) => `base64:${btoa(value)}`;
          translationValue = atob(valueOfTranslation.replaceAll(/base64:/g, ''));
        }
        if (TemplateEditorState.template?.type === 'sms') {
          callback = (value: string) => nl2br(value, true);
          translationValue = br2nl(valueOfTranslation);
        }

        // Create translations for each language of current shop
        const translationsToAdd: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
          section: sectionId ?? 0,
          language: language.id,
          fieldType,
          key,
          value: translationValue,
          callback,
        })) ?? [];

        // Merge with translations to add
        translationToAddToState = [...translationToAddToState, ...translationsToAdd];
      }
    });

    // Loop on matched translations as text
    Array.from(matchesTranslationsAsText).forEach((match) => {
      const key = match[1];
      const found = translationToAddToState.filter((translation) => translation.key === key);

      if (found.length === 0) {
        // Create empty translation because we don't know the value, for each language of current shop
        const translationsToAdd: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
          section: sectionId ?? 0,
          language: language.id,
          fieldType: 'text',
          key,
          value: '',
          callback: (value: string) => value,
        })) ?? [];

        // Merge with translations to add
        translationToAddToState = [...translationToAddToState, ...translationsToAdd];
      }
    });

    if (translationToAddToState.length) {
      addTranslationsToState(translationToAddToState);
    }
  }
};

interface SyncElement {
  name: string;
  syncElementId: string;
}

/**
 * Duplicate an element (html/css/translations) and add listeners
 * @param activeItemData
 * @param type
 * @param element
 * @param cssObj
 * @param position
 * @param translationsOfElement
 */
export const duplicateElement = async (
  activeItemData: ActiveLiveEditorItemData,
  type: TemplateStructureEnum,
  element: HTMLElement,
  cssObj: Maybe<Record<string, any>> = null,
  position: Maybe<string> = null,
  translationsOfElement: Maybe<Record<string, any>> = null,
  customWidgetCss: Maybe<Record<string, any>> = null,
  syncElement: { name: string; syncElementId: string; elementPartType?: ElementPartTypeEnum } | null = null,
  addHistory = true,
  toPlaceNextTo: Maybe<string> = null,
  replaceSectionId = '',
  keepSmartlistAttribute = false,
) => {
  // Fire-and-forget: start save point check now (captures pre-change state synchronously)
  // but don't block DOM changes. Promise is chained later for history ordering.
  const savePointPromise = !replaceSectionId ? checkIfSavePointNeeded() : Promise.resolve();

  const parentSection = element.closest(`.${TEMPLATE_SECTION_CLASS}`);
  let parentSectionId = parentSection?.getAttribute('data-spmelementid') ?? '';
  const oldId = element.id;
  let childrenIds: { [key: string]: string } = {};
  let lines: HTMLElement[];
  let columnsList: HTMLElement[];
  const newCssObj = cssObj;

  const smartlistAttributesToKeep = [
    's-engine',
    's-nb_weeks',
    's-ids',
    's-excluded_ids',
    's-excluded_categories',
    's-excluded_manufacturers',
  ];
  const smartlistAttributesToKeepValue: Record<string, string> = {};

  // First, we need to reset the smart products lists original codes (to duplicate the real HTML code)
  restoreSmartListsOriginalCode(element.id);

  // Clone element and duplicate its CSS
  const clonedElement = duplicateHtmlOfElement(type, element);

  // Hide element and insert after the source element
  clonedElement.style.display = 'none';

  clonedElement.classList.remove(BUILDER_ELEMENT_ACTIVE_CLASS, BUILDER_ELEMENT_HOVER_CLASS);
  if (syncElement) {
    if (!syncElement.elementPartType) {
      clonedElement.classList.add(TEMPLATE_SYNC_ELEMENT_CLASS);
    }
    clonedElement.setAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER, syncElement.syncElementId);
    clonedElement.setAttribute(TEMPLATE_SYNC_ELEMENT_NAME, syncElement.name);
    if (syncElement.elementPartType) {
      clonedElement.setAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE, syncElement.elementPartType);
    }
  }
  if (isEmailTemplate(TemplateEditorState.template?.type) && clonedElement.hasAttribute('data-widgettype')) {
    const parentElement = getSectionParent(element);
    if (parentElement
      && parentElement.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)
      && !parentElement.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)
      && element.hasAttribute(TEMPLATE_PART_ELEMENT_IDENTIFIER)
      && element.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)
      && element.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)
    ) {
      clonedElement.setAttribute(TEMPLATE_PART_ELEMENT_IDENTIFIER, generateUniqStructureId());
    }
  }

  const template = getTemplateIframeDocument();
  const sourceElement = template.querySelector(activeItemData.selector);

  if (!sourceElement) {
    return;
  }

  if (toPlaceNextTo) {
    const toPlaceNextToElement = template.querySelector(toPlaceNextTo);

    if (toPlaceNextToElement) {
      if (getWidgetType(toPlaceNextToElement.getAttribute('data-widgettype')) === WidgetTypeEnum.SMART_PRODUCT_LIST && keepSmartlistAttribute) {
        // Restore the smartlist original code BEFORE reading attributes
        restoreSmartListsOriginalCode(toPlaceNextToElement.id);

        // Keep smartlist attributes
        smartlistAttributesToKeep.forEach((attribute) => {
          const pattern = new RegExp(`${attribute}="([^"]*)"`, 'is');
          const find = toPlaceNextToElement.outerHTML.match(pattern);
          if (find) {
            // eslint-disable-next-line prefer-destructuring
            smartlistAttributesToKeepValue[attribute] = find[1];
          } else {
            smartlistAttributesToKeepValue[attribute] = '';
          }
        });

        Object.entries(smartlistAttributesToKeepValue).forEach(([attribute, value]) => {
          const attributeElement = clonedElement.querySelector(`[${attribute}]`);
          if (attributeElement) {
            attributeElement.setAttribute(attribute, value);
          }
        });
      }
      toPlaceNextToElement.after(clonedElement);
    }
  } else if (!toPlaceNextTo && (!position || position === 'bottom')) {
    sourceElement.after(clonedElement);
  } else {
    sourceElement.before(clonedElement);
  }

  if (type === TemplateStructureEnum.SECTION || type === TemplateStructureEnum.EMPTY_SECTION) {
    parentSectionId = clonedElement.getAttribute('data-spmelementid') ?? '';
  }
  // Get parent section id when drag and drop saved element
  if (position && !parentSectionId) {
    let sectionClass = `.${TEMPLATE_SECTION_CLASS}`;
    if (isDisplayTemplate(TemplateEditorState.template?.type)) {
      sectionClass = '#spm_body';
    }
    const closestSection = sourceElement.closest(sectionClass);
    parentSectionId = closestSection?.getAttribute('data-spmelementid') ?? '';
  }

  if (!newCssObj) {
    if (type === TemplateStructureEnum.WIDGET) {
      const oldWidgetId = element.id.replace(TEMPLATE_WIDGET_IDENTIFIER_PREFIX, '');
      const newWidgetId = clonedElement.id.replace(TEMPLATE_WIDGET_IDENTIFIER_PREFIX, '');

      let customCss: Record<string, any> | null = null;
      if (getElementFromIframe(`style[data-spm-styles="${CUSTOM_WIDGET_CSS_PREFIX}${oldWidgetId}"]`)) {
        customCss = {};
        const customWidgetOldCss = getCssOfElement(element.id, `${CUSTOM_WIDGET_CSS_PREFIX}${oldWidgetId}`);
        if (customWidgetOldCss) {
          customCss[`${CUSTOM_WIDGET_CSS_PREFIX}${newWidgetId}`] = customWidgetOldCss;
        }
        const customWidgetOldMobileCss = getCssOfElement(element.id, `${CUSTOM_WIDGET_CSS_PREFIX}${oldWidgetId}_mobile`);
        if (customWidgetOldMobileCss) {
          customCss[`${CUSTOM_WIDGET_CSS_PREFIX}${newWidgetId}_mobile`] = customWidgetOldMobileCss;
        }
      }
      if (customWidgetCss) {
        customCss = {};
        if (customWidgetCss[`${CUSTOM_WIDGET_CSS_PREFIX}${oldWidgetId}`]) {
          customCss[`${CUSTOM_WIDGET_CSS_PREFIX}${newWidgetId}`] = customWidgetCss[`${CUSTOM_WIDGET_CSS_PREFIX}${oldWidgetId}`];
        }
        if (customWidgetCss[`${CUSTOM_WIDGET_CSS_PREFIX}${oldWidgetId}_mobile`]) {
          customCss[`${CUSTOM_WIDGET_CSS_PREFIX}${newWidgetId}_mobile`] = customWidgetCss[`${CUSTOM_WIDGET_CSS_PREFIX}${oldWidgetId}_mobile`];
        }
      }
      duplicateCssOfElement(parentSectionId, oldId, clonedElement.id, null, customCss);
    } else {
      // Fallback normal pour les cas sans newCssString
      duplicateCssOfElement(parentSectionId, oldId, clonedElement.id, null, {
        dynamic: '',
        dynamic_persistent: '',
        static: '',
      });
    }
  } else if (type === TemplateStructureEnum.WIDGET) {
    // Apply CSS for widget when newCssObj exists (e.g., dragging synchronized widget)
    // Merge customWidgetCss and newCssObj to handle both custom widget CSS and normal CSS
    const cssToApply = customWidgetCss ? { ...newCssObj, ...customWidgetCss } : newCssObj;
    duplicateCssOfElement(parentSectionId, oldId, clonedElement.id, null, cssToApply);
  }
  const customCssTypes: string[] = [];
  // Get children if any
  if ([TemplateStructureEnum.LINE, TemplateStructureEnum.COLUMN, TemplateStructureEnum.SECTION, TemplateStructureEnum.EMPTY_SECTION].includes(type)) {
    childrenIds = { ...childrenIds, ...changeChildItemsId(clonedElement, TemplateStructureEnum.SECTION_CONTENT) };
    childrenIds = { ...childrenIds, ...changeChildItemsId(clonedElement, TemplateStructureEnum.LINE) };
    childrenIds = { ...childrenIds, ...changeChildItemsId(clonedElement, TemplateStructureEnum.COLUMN) };
    childrenIds = { ...childrenIds, ...changeChildItemsId(clonedElement, TemplateStructureEnum.WIDGET) };

    // 🚀 NOUVELLE APPROCHE: Traitement CSS global optimisé
    if (newCssObj) {
      // 1. Collecter tous les remplacements d'IDs
      const idReplacements = new Map<string, string>();
      idReplacements.set(oldId, clonedElement.id);

      // Ajouter tous les enfants
      Object.keys(childrenIds).forEach((oldChildId) => {
        idReplacements.set(oldChildId, childrenIds[oldChildId]);
      });

      // 2. Appliquer tous les remplacements sur chaque type de CSS
      Object.entries(newCssObj).forEach(([cssType, cssContent]) => {
        // 3. Appliquer les remplacements d'IDs sur le contenu CSS
        let finalCssString = String(cssContent);
        idReplacements.forEach((newId, oldId) => {
          const reg = new RegExp(`${oldId}([\\s,>+~.:\\[\\{\\)]|$)`, 'g');
          finalCssString = finalCssString.replace(reg, `${newId}$1`);
        });

        // 4. Ajouter au stylesheet avec le bon type
        appendCssToStylesheet(parentSectionId, finalCssString, cssType);
      });
    } else {
      // Fallback normal: traitement enfant par enfant avec duplicateCssOfElement
      Object.keys(childrenIds).forEach((key) => {
        const oldChildId = key;
        const newChildId: string = childrenIds[key];
        let customCss: Record<string, any> | null = null;

        if (customWidgetCss) {
          if (oldChildId.startsWith(TEMPLATE_WIDGET_IDENTIFIER_PREFIX)) {
            const widgetId = oldChildId.replace(TEMPLATE_WIDGET_IDENTIFIER_PREFIX, '');
            const newWidgetId = newChildId.replace(TEMPLATE_WIDGET_IDENTIFIER_PREFIX, '');
            customCss = {};
            if (customWidgetCss[`${CUSTOM_WIDGET_CSS_PREFIX}${widgetId}`]) {
              customCss[`${CUSTOM_WIDGET_CSS_PREFIX}${newWidgetId}`] = customWidgetCss[`${CUSTOM_WIDGET_CSS_PREFIX}${widgetId}`];
              customCssTypes.push(`${CUSTOM_WIDGET_CSS_PREFIX}${newWidgetId}`);
            }
            if (customWidgetCss[`${CUSTOM_WIDGET_CSS_PREFIX}${widgetId}_mobile`]) {
              customCss[`${CUSTOM_WIDGET_CSS_PREFIX}${newWidgetId}_mobile`] = customWidgetCss[`${CUSTOM_WIDGET_CSS_PREFIX}${widgetId}_mobile`];
              customCssTypes.push(`${CUSTOM_WIDGET_CSS_PREFIX}${newWidgetId}_mobile`);
            }
          }
        }
        if (!customCss) {
          duplicateCssOfElement(parentSectionId, oldChildId, newChildId, null, {
            dynamic: '',
            dynamic_persistent: '',
            static: '',
          });
        } else {
          duplicateCssOfElement(parentSectionId, oldChildId, newChildId, null, customCss);
        }
      });
    }
  }

  // Duplicate translations of element
  if (type === TemplateStructureEnum.SECTION || type === TemplateStructureEnum.EMPTY_SECTION) {
    duplicateTranslationsOfElement(clonedElement, translationsOfElement, clonedElement.getAttribute('data-spmelementid'));
  } else {
    duplicateTranslationsOfElement(clonedElement, translationsOfElement, parentSectionId);
  }

  switch (type) {
    case TemplateStructureEnum.LINE:
    case TemplateStructureEnum.COLUMN:
    case TemplateStructureEnum.SECTION:
    case TemplateStructureEnum.EMPTY_SECTION:
      // Remove buttons of builder
      removeChildActionsButtonGroup(clonedElement);

      // Add listeners
      if (type === TemplateStructureEnum.SECTION || type === TemplateStructureEnum.EMPTY_SECTION) {
        // If the new element is a section, we save it first in database to get its real ID
        await addNewSectionToState(clonedElement, ['dynamic', 'dynamic_persistent'], replaceSectionId);
        setClonedSectionContentAttributes(clonedElement);
        parentSectionId = clonedElement.getAttribute('data-spmelementid') ?? '';

        // Apply global margin to button align for all widgets with table.button in the section
        const widgetsInSection = clonedElement.querySelectorAll('[data-widgettype]');
        if (widgetsInSection && widgetsInSection.length > 0) {
          import('@/components/template-builder/setters').then(({ applyGlobalMarginToButtonAlign, applyGlobalBorderRadiusToButton }) => {
            widgetsInSection.forEach((widget) => {
              const widgetId = widget.getAttribute('id');
              if (widgetId) {
                applyGlobalMarginToButtonAlign(widgetId);
                // Apply global border-radius VML to button if needed
                applyGlobalBorderRadiusToButton(widgetId);
              }
            });
          });
        }
      }
      break;
    default:
      if (type === TemplateStructureEnum.WIDGET && syncElement) {
        addNewSyncElementToSection(parentSectionId, syncElement.syncElementId);
      }
      // Remove buttons of builder
      removeChildActionsButtonGroup(clonedElement);
      break;
  }

  // Mark section as active
  setActiveSection(parseInt(parentSectionId, 10));

  // Show duplicated element
  clonedElement.style.display = '';

  if (type === TemplateStructureEnum.COLUMN) {
    // Set width of columns only when duplicating a column (new column added to existing line)
    const parentRow: HTMLElement | null = clonedElement.closest(TEMPLATE_STRUCTURE_MAP[TemplateStructureEnum.LINE]);
    lines = parentRow ? [parentRow] : [];

    lines.forEach((line: HTMLElement) => {
      columnsList = Array.from(line.querySelectorAll(TEMPLATE_STRUCTURE_MAP[TemplateStructureEnum.COLUMN]));

      if (columnsList) {
        setChildColumnsWidth(columnsList);
      }
    });
  }

  // Move widget from section to another
  if (type === TemplateStructureEnum.WIDGET && toPlaceNextTo) {
    const toPlaceNextToElement = template.querySelector(toPlaceNextTo);
    const toPlaceNextToElementSection = toPlaceNextToElement?.closest(`.${TEMPLATE_SECTION_CLASS}`);
    if (toPlaceNextToElementSection) {
      const toPlaceNextToElementSectionId = toPlaceNextToElementSection.getAttribute('data-spmelementid');
      if (toPlaceNextToElementSectionId) {
        moveWidgetFromSectionToAnother(clonedElement.outerHTML, clonedElement.id, parentSectionId, toPlaceNextToElementSectionId);
      }
    }
  }

  // Reset listeners immediately so duplicated element is decorated and interactive
  resetActiveSection();
  resetIframe(clonedElement);

  // Defer heavy post-render work to after browser repaint for perceived instantaneousness
  // Chain on savePointPromise to ensure history ordering (intermediate save point before action history)
  setTimeout(() => {
    savePointPromise.then(() => {
      executeWhenDragComplete(() => {
        updateSectionsInState(false);

        if (isDisplayTemplate(TemplateEditorState.template?.type)) {
          updateFormsInState();
        }

        // Clean CSS
        cleanupDynamicStyle();
        if (addHistory) {
          // Prevent creating history in edit mode
          const selectedSyncElement = store.getters['liveEditor/getSelectedSyncElement'];
          if (!selectedSyncElement) {
            if (type === TemplateStructureEnum.LINE) {
              createHistory(HistoryType.DUPLICATE_ROW);
            } else if (type === TemplateStructureEnum.COLUMN) {
              createHistory(HistoryType.DUPLICATE_COLUMN);
            } else if (type === TemplateStructureEnum.WIDGET) {
              let historyType;
              if (position) {
                historyType = syncElement ? HistoryType.ADD_WIDGET_SYNC : HistoryType.ADD_WIDGET;
              } else {
                historyType = syncElement ? HistoryType.DUPLICATE_WIDGET_SYNC : HistoryType.DUPLICATE_WIDGET;
              }
              createHistory(historyType);
            } else if (type === TemplateStructureEnum.SECTION || type === TemplateStructureEnum.EMPTY_SECTION) {
              let historyType;
              if (position) {
                historyType = syncElement ? HistoryType.ADD_SECTION_SYNC : HistoryType.ADD_SECTION;
              } else {
                historyType = syncElement ? HistoryType.DUPLICATE_SECTION_SYNC : HistoryType.DUPLICATE_SECTION;
              }
              createHistory(historyType);
            }
          }
        }

        // Initialize widget via generic registry if duplicating (countdown, enkore, etc.)
        if (type === TemplateStructureEnum.WIDGET && clonedElement.id) {
          setTimeout(() => {
            initWidget({
              trigger: 'duplicate',
              widget: clonedElement,
              templateType: getTemplateParentTypeByType(TemplateEditorState.template?.type),
            });
          }, 100);
        }

      // Keep smartlist translations (title, button) and other configurations
      if (toPlaceNextTo) {
        const toPlaceNextToElement = template.querySelector(toPlaceNextTo);

        if (toPlaceNextToElement) {
          if (getWidgetType(toPlaceNextToElement.getAttribute('data-widgettype')) === WidgetTypeEnum.SMART_PRODUCT_LIST && keepSmartlistAttribute) {
            // Keep smartlist sync element
            if (toPlaceNextToElement.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) && !toPlaceNextToElement.getAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)) {
              clonedElement.setAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER, toPlaceNextToElement.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) || '');
              clonedElement.setAttribute(TEMPLATE_SYNC_ELEMENT_NAME, toPlaceNextToElement.getAttribute(TEMPLATE_SYNC_ELEMENT_NAME) || '');
              clonedElement.removeAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE);

              updateSectionsInState(false);
            }

            // Smart list title
            const smartlistTitle = getSmartListTitle(`#${toPlaceNextToElement.id} .spm_product_list_title`, '', toPlaceNextToElement.id);
            setSmartListTitle(`#${clonedElement.id} .spm_product_list_title`, {
              value: smartlistTitle, name: '', getter: () => {}, setters: [],
            }, clonedElement.id);

            // Smart list button
            let smartlistGetButtonSelector = isDisplayTemplate(TemplateEditorState.template?.type) ? 'a.spm_product_list_element_button' : '.spm_product_list_element_button > tbody > tr > th > a';
            const smartlistSetButtonSelector = `#${clonedElement.id} ${smartlistGetButtonSelector}`;
            smartlistGetButtonSelector = `#${toPlaceNextToElement.id} ${smartlistGetButtonSelector}`;
            const smartlistButton = getSmartListContent(smartlistGetButtonSelector, '', toPlaceNextToElement.id);
            setSmartListButtonTitle(smartlistSetButtonSelector, {
              value: smartlistButton, name: '', getter: () => {}, setters: [],
            }, clonedElement.id);
          }
        }
      }
      });
    });
  }, 0);

  // eslint-disable-next-line consistent-return
  return clonedElement.id;
};

export const onDuplicateWidget = (activeItemData: ActiveLiveEditorItemData) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template.querySelector(activeItemData.selector);

  if (element) {
    // Get widget type from data attribute
    const widgetType = element.getAttribute('data-widgettype');
    if (widgetType) {
      // Convert from data-widgettype format to WidgetTypeEnum format
      const widgetTypeEnum = widgetType.replace('spm_widget_', 'widget_').replaceAll('_', '-');
      // Validate widget duplication
      const validation = validateWidgetAddition(widgetTypeEnum);
      if (!validation.canAdd) {
        // Show error message and cancel duplication
        if (validation.message) {
          // You can add a toast notification here
          showToastError(validation.message, 3000, true);
        }
        return;
      }
    }

    duplicateElement(activeItemData, TemplateStructureEnum.WIDGET, element);
  }
};

export const onDuplicateColumn = (activeItemData: ActiveLiveEditorItemData) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template.querySelector(activeItemData.selector);

  if (element) {
    const columnList = element?.parentElement?.children;

    if (columnList && columnList.length >= MAX_COLUMN_COUNT) {
      showToastError('MAX_COLUMNS_COUNT');
      return;
    }

    duplicateElement(activeItemData, TemplateStructureEnum.COLUMN, element);
  }
};

export const onDuplicateLine = (activeItemData: ActiveLiveEditorItemData) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template.querySelector(activeItemData.selector);

  if (element) {
    duplicateElement(activeItemData, TemplateStructureEnum.LINE, element);
  }
};

export const onDuplicateSection = (activeItemData: ActiveLiveEditorItemData) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template.querySelector(activeItemData.selector);

  if (element) {
    duplicateElement(activeItemData, TemplateStructureEnum.SECTION, element);
  }
};

// common action handlers
export const onRemoveElement = (selector: string) => {
  const template = getTemplateIframeDocument();
  const elementToRemove: HTMLElement | null = template.querySelector(selector);
  hideLeftToolbar();
  if (elementToRemove) {
    elementToRemove.remove();
  }
};

export const removeColumn = (event: any, column: HTMLElement) => {
  event.preventDefault();
  event.stopPropagation();

  const columnId = column.id;
  if (columnId) {
    const acceptCallback = () => {
      // Fire-and-forget: start save point check now (captures pre-change state synchronously)
      const savePointPromise = checkIfSavePointNeeded();

      // CRITICAL SECURITY CHECK: Prevent deletion if this is the last column in the template
      const allColumnsBeforeDelete = getTemplateColumns();
      const allSectionsBeforeDelete = getTemplateSections();

      // If this is the only column in the entire template, ABORT deletion
      if (allColumnsBeforeDelete.length <= 1) {
        showToastError('Impossible de supprimer la dernière colonne du modèle');
        return;
      }

      const section = column.closest('[data-spmelementid]');

      if (section) {
        const sectionId = section.getAttribute('data-spmelementid');

        if (sectionId) {
          // Mark section as active
          setActiveSection(parseInt(sectionId, 10));
        }

        // Get parent row
        const rows = getParents(column, TEMPLATE_LINE_CLASS);

        // IMMEDIATE DOM removal
        onRemoveElement(`#${column.getAttribute('id')}`);

        if (rows && rows.length) {
          const row = rows[0];
          const columnsInRow = Array.from(row.querySelectorAll(WIDGET_DROPPABLE_CLASS));
          // Remove row if there is no column inside
          if (columnsInRow.length === 0) {
            const linesInSection = Array.from(section.querySelectorAll(TEMPLATE_LINE_CLASS));
            // Only remove row if there are other lines in section OR other sections
            if (linesInSection.length > 1 || allSectionsBeforeDelete.length > 1) {
              onRemoveElement(`#${row.getAttribute('id')}`);
              // Check if section is now empty after row removal
              const remainingLinesInSection = Array.from(section.querySelectorAll(TEMPLATE_LINE_CLASS));
              // Remove section if there is no line inside AND there are other sections
              if (remainingLinesInSection.length === 0 && allSectionsBeforeDelete.length > 1) {
                onRemoveElement(`#${section.getAttribute('id')}`);
              }
            }
          }
        }
      }

      // Reset listeners immediately so remaining elements are decorated and interactive
      resetListeners();
      resetActiveSection();

      // Defer heavy work to after browser repaint, chained on savePointPromise for history ordering.
      // IMPORTANT: updateSectionsInState triggers Vue reactivity which can unmount/remount WidgetsPanel.
      // If a drag is in progress, this would destroy the DOM element being dragged → stuck drag.
      // We wait until no drag is active before executing.
      setTimeout(() => {
        savePointPromise.then(() => {
          executeWhenDragComplete(() => {
            updateSectionsInState(false);

            // Prevent creating history entry in edit mode
            const currentSyncElement = store.getters['liveEditor/getSelectedSyncElement'];
            if (!currentSyncElement) {
              createHistory(HistoryType.DELETE_COLUMN);
            }

            // Cleanup widget static CSS for any widget types no longer present
            cleanupAllUnusedWidgetCss();

            refreshSectionsConfig();
            refreshTranslations();
          });
        });
      }, 0);
    };

    // SYNC ELEMENT WARNING
    const selectedSyncElement = store.getters['liveEditor/getSelectedSyncElement'];
    const sectionParent = getSectionParent(column);
    if (sectionParent) {
      const elementSyncId = sectionParent.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER);
      const sectionId = sectionParent.getAttribute('id');
      if (elementSyncId && !selectedSyncElement) {
        store.dispatch('liveEditor/showSyncElementWarning', {
          elementId: sectionParent.getAttribute('id'),
          elementType: TemplateStructureEnum.SECTION,
          acceptCallback,
          rejectCallback: null,
        });
      } else if (sectionId && selectedSyncElement && sectionId === selectedSyncElement.elementId) {
        acceptCallback();
      } else if (!elementSyncId && !selectedSyncElement) {
        acceptCallback();
      }
    } else {
      acceptCallback();
    }
  }
};
